/**
 * The tenant onboarding wizard's vocabulary (TO-190): the ordered step
 * catalogue, a step's status, and the read model the tenant-plane
 * `/t/{slug}/admin/onboarding` routes return.
 */

/**
 * A wizard step. Adding a member here is a compile error until it is also given
 * a `required` flag in {@link ONBOARDING_STEP_REQUIRED} and a display position in
 * {@link ONBOARDING_STEP_ORDER} — both are exhaustive over this enum — plus a
 * wizard registry entry on the frontend (`Record<OnboardingStep, …>`).
 */
export enum OnboardingStep {
	IDENTITY = 'identity',
	LOCALES = 'locales',
	ORGANIZATION = 'organization',
	FILE_EXCHANGE = 'file-exchange',
	CONTENT_REVIEW = 'content-review',
}

/** A step's status; a step with no stored entry is `NOT_STARTED`. */
export enum OnboardingStepStatus {
	NOT_STARTED = 'not_started',
	IN_PROGRESS = 'in_progress',
	COMPLETE = 'complete',
	SKIPPED = 'skipped',
}

/** The statuses a PATCH may request — never `NOT_STARTED`. */
export const ONBOARDING_SETTABLE_STATUSES = [
	OnboardingStepStatus.IN_PROGRESS,
	OnboardingStepStatus.COMPLETE,
	OnboardingStepStatus.SKIPPED,
] as const

/** The aggregate of the tenant's starter-data seeders, as a Tenant Admin sees it. */
export enum OnboardingSeedingStatus {
	PENDING = 'PENDING',
	COMPLETED = 'COMPLETED',
	FAILED = 'FAILED',
}

/** One catalogue entry: a required step cannot be skipped. */
export interface OnboardingStepDefinition {
	step: OnboardingStep
	required: boolean
}

/**
 * Whether each step is required. A `Record` over the enum, so dropping a step
 * is a compile error (exhaustiveness) rather than a silent runtime "optional,
 * absent step" — the catalogue below is derived from this, never hand-kept in
 * sync.
 */
const ONBOARDING_STEP_REQUIRED: Record<OnboardingStep, boolean> = {
	[OnboardingStep.IDENTITY]: true,
	[OnboardingStep.LOCALES]: true,
	[OnboardingStep.ORGANIZATION]: true,
	[OnboardingStep.FILE_EXCHANGE]: false,
	[OnboardingStep.CONTENT_REVIEW]: false,
}

/** The wizard's steps in display order (TO-190 REQ-001). */
const ONBOARDING_STEP_ORDER = [
	OnboardingStep.IDENTITY,
	OnboardingStep.LOCALES,
	OnboardingStep.ORGANIZATION,
	OnboardingStep.FILE_EXCHANGE,
	OnboardingStep.CONTENT_REVIEW,
] as const

// Compile-time guard: ONBOARDING_STEP_ORDER must list every enum member. If a step
// is added to OnboardingStep but not here, `MissingStep` is a non-`never` union and
// this assignment fails to type-check (a `never` value cannot hold a real member).
type MissingStep = Exclude<OnboardingStep, (typeof ONBOARDING_STEP_ORDER)[number]>
const _ONBOARDING_STEP_ORDER_IS_EXHAUSTIVE: never[] = [] as MissingStep[]
void _ONBOARDING_STEP_ORDER_IS_EXHAUSTIVE

/**
 * The wizard's steps, in display order (TO-190 REQ-001). Derived from the
 * exhaustive {@link ONBOARDING_STEP_REQUIRED} record and {@link ONBOARDING_STEP_ORDER},
 * so a step can never silently be missing its `required` flag or its position.
 */
export const ONBOARDING_STEP_CATALOGUE: readonly OnboardingStepDefinition[] = ONBOARDING_STEP_ORDER.map((step) => ({
	step,
	required: ONBOARDING_STEP_REQUIRED[step],
}))

/** A stored step entry inside `tenants.onboarding_progress`. */
export interface OnboardingProgressEntry {
	status: OnboardingStepStatus
	/** ISO-8601 UTC. */
	updatedAt: string
}

/** `tenants.onboarding_progress`: one entry per step that has left `NOT_STARTED`. */
export type OnboardingProgress = Partial<Record<OnboardingStep, OnboardingProgressEntry>>

export interface OnboardingStepDTO {
	step: OnboardingStep
	required: boolean
	status: OnboardingStepStatus
	updatedAt: string | null
}

export interface OnboardingSeedingSummaryDTO {
	status: OnboardingSeedingStatus
	/** The failed contexts' names, ascending. */
	failedContexts: string[]
}

/** `GET|PATCH /t/{slug}/admin/onboarding` → 200. */
export interface TenantOnboardingDTO {
	tenantId: string
	completedAt: string | null
	steps: OnboardingStepDTO[]
	seeding: OnboardingSeedingSummaryDTO
}

/** `PATCH /t/{slug}/admin/onboarding` body. */
export interface TenantOnboardingStepPatch {
	step: OnboardingStep
	status: (typeof ONBOARDING_SETTABLE_STATUSES)[number]
}
